import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import { PaintBrush as StyleIcon } from "@phosphor-icons/react/PaintBrush";
import { Question as QuestionIcon } from "@phosphor-icons/react/Question";
import Link from "next/link";
import { FC } from "react";
import { Option } from "~ui-components/components/atoms/option";
import {
	GEOMETRY_TYPE,
	LAYER_STYLE_TYPE,
} from "~ui-components/types/__generated/gql/graphql";

interface Slots {
	SimpleStyle: FC;
	CategoryStyle: FC;
	QuantityStyle: FC;
	BubbleStyle: FC;
	HeatmapStyle: FC;
	CustomSLD: FC;
}

const Empty = () => null;

const defaultSlots: Slots = {
	SimpleStyle: Empty,
	CategoryStyle: Empty,
	QuantityStyle: Empty,
	BubbleStyle: Empty,
	HeatmapStyle: Empty,
	CustomSLD: Empty,
};

interface LayerStyleProps {
	visualisation: VISUALISATION_TYPE;
	onVisualisationChange: (value: VISUALISATION_TYPE) => void;
	geometryType: GEOMETRY_TYPE;
	slots?: Slots;
}

export type VISUALISATION_TYPE =
	| "simple"
	| "category"
	| "quantity"
	| "bubble"
	| "heatmap";

const defaultVisualizationOptions: {
	value: VISUALISATION_TYPE;
	label: string;
}[] = [
	{ value: "simple", label: "Simple" },
	{ value: "category", label: "Category" },
	{ value: "quantity", label: "Quantity" },
	{ value: "bubble", label: "Bubble" },
];

const pointDataVisualizationOptions: {
	value: VISUALISATION_TYPE;
	label: string;
}[] = [...defaultVisualizationOptions, { value: "heatmap", label: "Heatmap" }];

export const layerStyleTypeToVisualisationType: Record<
	Exclude<LAYER_STYLE_TYPE, LAYER_STYLE_TYPE.WMS | LAYER_STYLE_TYPE.RASTER>,
	VISUALISATION_TYPE
> = {
	[LAYER_STYLE_TYPE.BUBBLE]: "bubble",
	[LAYER_STYLE_TYPE.HEATMAP]: "heatmap",
	[LAYER_STYLE_TYPE.LINE_CATEGORY]: "category",
	[LAYER_STYLE_TYPE.LINE_QUANTITY]: "quantity",
	[LAYER_STYLE_TYPE.LINE_SIMPLE]: "simple",
	[LAYER_STYLE_TYPE.POINT_CATEGORY]: "category",
	[LAYER_STYLE_TYPE.POINT_QUANTITY]: "quantity",
	[LAYER_STYLE_TYPE.POINT_SIMPLE]: "simple",
	[LAYER_STYLE_TYPE.POLYGON_CATEGORY]: "category",
	[LAYER_STYLE_TYPE.POLYGON_QUANTITY]: "quantity",
	[LAYER_STYLE_TYPE.POLYGON_SIMPLE]: "simple",
};

export const LayerStyle: FC<LayerStyleProps> = ({
	visualisation,
	slots: Slots = defaultSlots,
	geometryType,
	onVisualisationChange,
}) => {
	const visualizationOptions =
		geometryType === GEOMETRY_TYPE.POINT
			? pointDataVisualizationOptions
			: defaultVisualizationOptions;

	return (
		<>
			<Card>
				<CardHeader
					avatar={
						<Avatar>
							<StyleIcon fontSize="var(--Icon-fontSize)" />
						</Avatar>
					}
					title="Layer style"
					action={
						<Link
							href={
								"https://help.mangomap.com/hc/en-us/articles/360022122592-The-Layers-panel"
							}
							target="_blank">
							<IconButton aria-label="settings">
								<QuestionIcon />
							</IconButton>
						</Link>
					}
				/>
				<CardContent>
					<Stack spacing={3}>
						<FormControl>
							<InputLabel>Visualisation type</InputLabel>
							<Select
								value={visualisation}
								onChange={(event) =>
									onVisualisationChange(
										event.target.value as VISUALISATION_TYPE,
									)
								}>
								{visualizationOptions.map((visualization) => (
									<Option
										key={visualization.value}
										value={visualization.value}>
										{visualization.label}
									</Option>
								))}
							</Select>
						</FormControl>

						{visualisation === "simple" && <Slots.SimpleStyle />}
						{visualisation === "category" && <Slots.CategoryStyle />}
						{visualisation === "quantity" && <Slots.QuantityStyle />}
						{visualisation === "bubble" && <Slots.BubbleStyle />}
						{visualisation === "heatmap" && <Slots.HeatmapStyle />}
						{/* <Slots.CustomSLD /> */}
					</Stack>
				</CardContent>
			</Card>
		</>
	);
};
